/**
 * Menu Configuration for Side Navigation
 *
 * Defines the structure of the icon-based navigation system:
 * - 6 groups: search, view, data, management, integrity, settings
 * - Each group contains 1-5 menu items
 * - Items can be: panel (opens secondary palette), route (navigates), or submenu (expandable)
 */

import permissions from './permissions';
import {
  PANEL_IDS,
  isHierarchicalPanel as isPanelHierarchical,
} from '@/constants/panelIds';

/**
 * Menu Groups Configuration
 * Each group appears as an icon in the 50px icon strip
 */
export const MENU_GROUPS = {
  search: {
    id: 'search',
    title: 'Global Search',
    icon: 'mdi-magnify',
    order: 1,
    position: 'main',
    directPanel: PANEL_IDS.GLOBAL_SEARCH, // Opens panel directly without expanding rail
    items: [], // Leaf node - no sub-items, clicking icon directly opens panel
  },
  view: {
    id: 'view',
    title: 'View',
    icon: 'mdi-eye-outline',
    order: 2,
    position: 'main',
    items: [
      {
        id: 'plans-2d',
        title: '2D Plans',
        icon: 'mdi-floor-plan',
        type: 'panel',
        permission: [permissions.PlotPlans2D.Access],
      },
      {
        id: 'scans',
        title: 'Scans (360)',
        icon: 'mdi-camera',
        type: 'panel',
        permission: [permissions.Images360.View],
      },
      {
        id: 'models',
        title: 'Models',
        icon: 'mdi-cube-outline',
        type: 'panel',
        permission: [permissions.ProjectModels.View],
      },
      {
        id: 'bookmarks',
        title: 'Saved Views',
        icon: 'mdi-bookmark-multiple-outline',
        type: 'panel',
        permission: [permissions.Bookmarks.View],
        requiresModel: true,
      },
      {
        // Shared zones palette (AVA-765).
        id: 'zones',
        title: 'Zones',
        icon: 'mdi-vector-square',
        type: 'panel',
        permission: [permissions.Zones.View],
      },
    ],
  },
  data: {
    id: 'data',
    title: 'Data & Docs',
    icon: 'mdi-database-outline',
    order: 3,
    position: 'main',
    items: [
      {
        id: 'tags',
        title: 'Tags',
        icon: 'mdi-tag-multiple-outline',
        type: 'panel',
        permission: [
          permissions.Tags.Container.View,
          permissions.Tags.Equipment.View,
          permissions.Tags.General.View,
          permissions.Tags.Permit.View,
          permissions.Tags.Pipeline.View,
          permissions.Tags.Scaffold.View,
          permissions.Tags.Structural.View,
        ],
      },
      {
        id: 'documents',
        title: 'Documents',
        icon: 'mdi-file-document-multiple-outline',
        type: 'panel',
        permission: [permissions.Documents.View],
      },
      {
        id: 'scan-download',
        title: 'Scan Download',
        icon: 'mdi-cloud-download-outline',
        type: 'panel',
        permission: [permissions.CustomPointcloudDownload.Create],
        requiresModel: true,
      },
    ],
  },
  management: {
    id: 'management',
    title: 'Management',
    icon: 'mdi-clipboard-list-outline',
    order: 4,
    position: 'main',
    items: [
      {
        id: 'registers',
        title: 'Registers',
        icon: 'mdi-clipboard-list',
        type: 'panel',
        permission: [permissions.Registers.View],
      },
      {
        id: 'workspaces',
        title: 'Workspaces',
        icon: 'mdi-view-dashboard-outline',
        type: 'panel',
        directPanel: PANEL_IDS.WORKSPACES,
        permission: [
          permissions.TaskTracker.View,
          permissions.Gantt.View,
          permissions.Scheduler.View,
        ],
      },
      {
        id: 'dashboards',
        title: 'Explorer',
        icon: 'mdi-chart-box-outline',
        type: 'route',
        routePath: '/dashboard/custom',
        permission: [permissions.CustomDashboards.View],
      },
    ],
  },
  integrity: {
    id: 'integrity',
    title: 'Integrity',
    icon: 'mdi-shield-check-outline',
    order: 5,
    position: 'main',
    items: [
      {
        id: 'integrity-hub',
        title: 'Analysis',
        icon: 'mdi-eye-check-outline',
        type: 'route',
        routePath: '/dashboard/inspection/ai-analysis',
        permission: [
          [permissions.Images360.View, permissions.AIAnalysis.View],
          permissions.Dashboard.Inspections_CorrosionAnalyser,
        ],
      },
      {
        id: 'condition-map',
        title: 'Inspect',
        icon: 'mdi-map-marker-check-outline',
        type: 'panel',
        permission: [permissions.InspectionTags.View],
      },
    ],
  },
  settings: {
    id: 'settings',
    title: 'Settings',
    icon: 'mdi-cog-outline',
    order: 6,
    position: 'footer',
    items: [
      // quick-settings panel removed - now using QuickSettingsFloating in view.vue
      // triggered by dedicated button in ExpandableRail footer
      {
        id: 'admin',
        title: 'Facility Admin',
        icon: 'mdi-shield-crown-outline',
        type: 'route',
        routePath: '/admin',
        permission: [permissions.Admin.Access],
      },
    ],
  },
};

/**
 * Panel Components - Lazy loaded Vue components
 * Maps panel IDs to their component imports for code splitting
 */
export const PANEL_COMPONENTS = {
  [PANEL_IDS.GLOBAL_SEARCH]: () => import('@/components/search/globalSearch.vue'),
  [PANEL_IDS.BOOKMARKS]: () => import('@/components/bookmarks/dashboardTab.vue'),
  [PANEL_IDS.PLANS_2D]: () => import('@/components/plans2d/plan2dTab.vue'),
  [PANEL_IDS.SCANS]: () => import('@/components/scans/scansTab.vue'),
  [PANEL_IDS.MODELS]: () => import('@/components/models/modelsTab.vue'),
  [PANEL_IDS.ZONES]: () => import('@/features/Zones/components/ZonesPanel.vue'),
  [PANEL_IDS.TAGS]: () => import('@Tags/components/tagsTab.vue'),
  [PANEL_IDS.DOCUMENTS]: () => import('@/components/attachments/attachmentsTab.vue'),
  [PANEL_IDS.SCAN_DOWNLOAD]: () => import('@/components/pointcloudDownload/pointCloudsDownloadTab.vue'),
  [PANEL_IDS.CONDITION_MAP]: () => import('@/components/tags/amisTab.vue'),
  [PANEL_IDS.INSPECTION_INFO]: () => import('@Tags/components/inspection/InspectionInfoPanel.vue'),
  [PANEL_IDS.AVA_INSPECTION_INFO]: () => import('@Tags/components/avaInspect/AvaInspectInfoPanel.vue'),
  [PANEL_IDS.DISPLAY_SETTINGS]: () => import('@/components/settings/displaySettings.vue'),
  [PANEL_IDS.REGISTERS]: () => import('@/components/registers/registersPanel.vue'),
  [PANEL_IDS.WORKSPACES]: () => import('@/components/workspaces/workspacesPanel.vue'),

  // Type-specific tag panels (for side-by-side multi-panel view)
  [PANEL_IDS.TAGS_EQUIPMENT]: () => import('@/components/tags/tagsList.vue'),
  [PANEL_IDS.TAGS_PIPELINE]: () => import('@/components/tags/tagsList.vue'),
  [PANEL_IDS.TAGS_STRUCTURAL]: () => import('@/components/tags/tagsList.vue'),
  [PANEL_IDS.TAGS_CONTAINER]: () => import('@/components/tags/tagsList.vue'),
  [PANEL_IDS.TAGS_SCAFFOLD]: () => import('@/components/tags/tagsList.vue'),
  [PANEL_IDS.TAGS_GENERAL]: () => import('@/components/tags/tagsList.vue'),
  [PANEL_IDS.TAGS_PERMIT]: () => import('@/components/tags/tagsList.vue'),
  [PANEL_IDS.TAGS_ISOLATION]: () => import('@/components/tags/tagsList.vue'),
  [PANEL_IDS.TAGS_ANOMALY]: () => import('@/components/tags/tagsList.vue'),
  [PANEL_IDS.TAGS_OPERATION]: () => import('@/components/tags/tagsList.vue'),
};

/**
 * Panel titles that can change based on context (hierarchical panels)
 */
export const DYNAMIC_PANEL_TITLES = {
  [PANEL_IDS.GLOBAL_SEARCH]: {
    fallback: 'Global Search',
  },
  [PANEL_IDS.TAGS]: {
    getter: 'GET_EDITING_TAG',
    fallback: 'Tags',
    property: 'name',
  },
  [PANEL_IDS.CONDITION_MAP]: {
    fallback: 'Inspect',
  },
  [PANEL_IDS.INSPECTION_INFO]: {
    getter: 'GET_EDITING_AMIS_TAG',
    fallback: 'Inspection',
    property: 'name',
  },
  [PANEL_IDS.AVA_INSPECTION_INFO]: {
    getter: 'GET_EDITING_AMIS_TAG',
    fallback: 'AVA Inspection',
    property: 'name',
  },
  [PANEL_IDS.DISPLAY_SETTINGS]: {
    fallback: 'Display Settings',
  },
  [PANEL_IDS.REGISTERS]: {
    fallback: 'Registers',
  },
  [PANEL_IDS.WORKSPACES]: {
    fallback: 'Workspaces',
  },
  [PANEL_IDS.ZONES]: {
    fallback: 'Zones',
  },
  // Type-specific tag panel titles
  [PANEL_IDS.TAGS_EQUIPMENT]: { fallback: 'Equipment' },
  [PANEL_IDS.TAGS_PIPELINE]: { fallback: 'Pipeline' },
  [PANEL_IDS.TAGS_STRUCTURAL]: { fallback: 'Structural' },
  [PANEL_IDS.TAGS_CONTAINER]: { fallback: 'Container' },
  [PANEL_IDS.TAGS_SCAFFOLD]: { fallback: 'Scaffold' },
  [PANEL_IDS.TAGS_GENERAL]: { fallback: 'General' },
  [PANEL_IDS.TAGS_PERMIT]: { fallback: 'Permit' },
  [PANEL_IDS.TAGS_ISOLATION]: { fallback: 'Isolation' },
  [PANEL_IDS.TAGS_ANOMALY]: { fallback: 'Anomaly' },
  [PANEL_IDS.TAGS_OPERATION]: { fallback: 'Operation' },
};

// ============================================
// Helper Functions
// ============================================

/**
 * Get a menu group by ID
 * @param {string} groupId - The group ID
 * @returns {Object|undefined} The menu group or undefined
 */
export function getGroupById(groupId) {
  return MENU_GROUPS[groupId];
}

/**
 * Get a menu item by ID (searches all groups)
 * @param {string} itemId - The item ID
 * @returns {Object|undefined} The menu item or undefined
 */
export function getItemById(itemId) {
  for (const group of Object.values(MENU_GROUPS)) {
    const item = group.items.find((i) => i.id === itemId);
    if (item) {
      return { ...item, groupId: group.id };
    }
  }
  return undefined;
}

/**
 * Get all items for a group
 * @param {string} groupId - The group ID
 * @returns {Array} Array of menu items
 */
export function getItemsForGroup(groupId) {
  const group = MENU_GROUPS[groupId];
  return group ? group.items : [];
}

/**
 * Get groups sorted by order
 * @param {string} position - Filter by position ('main', 'footer', or undefined for all)
 * @returns {Array} Sorted array of groups
 */
export function getGroupsSorted(position) {
  const groups = Object.values(MENU_GROUPS);
  const filtered = position ? groups.filter((g) => g.position === position) : groups;
  return filtered.sort((a, b) => a.order - b.order);
}

/**
 * Check if a panel is hierarchical (has multi-level navigation)
 * Re-exports from centralized panelIds constants for backwards compatibility.
 * @param {string} panelId - The panel ID
 * @returns {boolean}
 */
export function isHierarchicalPanel(panelId) {
  return isPanelHierarchical(panelId);
}

/**
 * Get the dynamic title config for a panel
 * @param {string} panelId - The panel ID
 * @returns {Object|undefined}
 */
export function getDynamicTitleConfig(panelId) {
  return DYNAMIC_PANEL_TITLES[panelId];
}

/**
 * Check remaining capacity for a group (max 5 items)
 * @param {string} groupId - The group ID
 * @returns {Object} { hasSpace: boolean, remainingSlots: number }
 */
export function checkGroupCapacity(groupId) {
  const group = MENU_GROUPS[groupId];
  if (!group) {
    return { hasSpace: false, remainingSlots: 0 };
  }
  const currentCount = group.items.length;
  const maxItems = 5;
  return {
    hasSpace: currentCount < maxItems,
    remainingSlots: Math.max(0, maxItems - currentCount),
  };
}

/**
 * Validate the menu configuration
 * @returns {Object} { valid: boolean, errors: string[] }
 */
export function validateMenuConfig() {
  const errors = [];
  const allItemIds = new Set();

  for (const [groupId, group] of Object.entries(MENU_GROUPS)) {
    // Check group has required fields
    if (!group.id || !group.title || !group.icon) {
      errors.push(`Group '${groupId}' missing required fields`);
    }

    // Check group order is valid (1-6)
    if (group.order < 1 || group.order > 6) {
      errors.push(`Group '${groupId}' has invalid order: ${group.order}`);
    }

    // Check items count (1-5)
    if (!group.items || group.items.length === 0) {
      errors.push(`Group '${groupId}' has no items`);
    } else if (group.items.length > 5) {
      errors.push(`Group '${groupId}' exceeds max 5 items`);
    }

    // Check each item
    for (const item of group.items || []) {
      if (!item.id || !item.title || !item.type) {
        errors.push(`Item in group '${groupId}' missing required fields`);
      }

      // Check unique IDs
      if (allItemIds.has(item.id)) {
        errors.push(`Duplicate item ID: ${item.id}`);
      }
      allItemIds.add(item.id);

      // Check type-specific requirements
      if (item.type === 'panel' && !PANEL_COMPONENTS[item.id]) {
        errors.push(`Panel item '${item.id}' has no component mapping`);
      }
      if (item.type === 'route' && !item.routePath) {
        errors.push(`Route item '${item.id}' has no routePath`);
      }
      if (item.type === 'submenu' && !item.submenuGetter) {
        errors.push(`Submenu item '${item.id}' has no submenuGetter`);
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

export default {
  MENU_GROUPS,
  PANEL_COMPONENTS,
  DYNAMIC_PANEL_TITLES,
  getGroupById,
  getItemById,
  getItemsForGroup,
  getGroupsSorted,
  isHierarchicalPanel,
  getDynamicTitleConfig,
  checkGroupCapacity,
  validateMenuConfig,
};
